import React, { useState, useEffect } from "react";
import { BarChart } from "@mui/x-charts";
import { Select, } from "antd";
import { getRoute } from "../api/routes";
import api from "../Interceptor";
import { CustomInput } from "./ui";

const { Option } = Select;

const getCurrentWeekDays = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - dayOfWeek);

  const weekDays = [];
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    weekDays.push(dayNames[day.getDay()]);
  }

  return weekDays;
};

const getLast6Years = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => currentYear - i).reverse();
};

const getAllMonths = () => [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const User_ExpenseGraph = ({
  selectedProject,
  selectedMilestone,
  selectedDuration,
  startDate,
  endDate,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expenseData, setExpenseData] = useState([]);
  const [showIncome, setShowIncome] = useState(true);
  const [showExpense, setShowExpense] = useState(true);
  const [showProfit, setShowProfit] = useState(true);

  const getEmptyDataset = () => {
    if (selectedDuration === "last12months") {
      return getAllMonths().map((month) => ({
        day: month,
        totalIncome: 0,
        totalExpense: 0,
        profit: 0,
      }));
    } else if (selectedDuration === "currentweek") {
      return getCurrentWeekDays().map((day) => ({
        day,
        totalIncome: 0,
        totalExpense: 0,
        profit: 0,
      }));
    } else if (selectedDuration === "last6years") {
      return getLast6Years().map((year) => ({
        day: year.toString(),
        totalIncome: 0,
        totalExpense: 0,
        profit: 0,
      }));
    } else if (selectedDuration === "all") {
      // Group data by all available years
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear; i >= currentYear - 10; i--) {
        // Adjust the range as needed
        years.push(i.toString());
      }
      return years.map((year) => ({
        day: year,
        totalIncome: 0,
        totalExpense: 0,
        profit: 0,
      }));
    }
    return [];
  };
  

  const fetchFilteredExpenses = async (
    selectedProject,
    selectedMilestone,
    startDate,
    endDate
  ) => {
    try {
      setLoading(true);
      setError(null);

      let url = `${getRoute("users", "user", "expenses", false)}`;
      let params = [];
      if (selectedProject !== "All") {
        params.push(`projectId=${selectedProject}`);
      }
      if (selectedMilestone !== "All") {
        params.push(`milestoneId=${selectedMilestone}`);
      }
      if (startDate) {
        params.push(`startDate=${startDate}`);
      }
      if (endDate) {
        params.push(`endDate=${endDate}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }

      const response = await api.get(url);
      const payload = response?.data;
      const allExpenses = Array.isArray(payload)
        ? payload
        : payload?.expenses || [];

      setExpenseData(allExpenses);
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const currentWeekDays = getCurrentWeekDays();

      let groupedData = {};

      if (selectedDuration === "last12months") {
        const last12Months = [];
        for (let i = 11; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          last12Months.push(date.toLocaleString("default", { month: "short" }));
        }

        groupedData = last12Months.reduce((acc, month) => {
          acc[month] = { totalIncome: 0, totalExpense: 0, profit: 0 };
          return acc;
        }, {});

        allExpenses.forEach((expense) => {
          const expenseDate = new Date(expense.date);
          const month = expenseDate.toLocaleString("default", {
            month: "short",
          });

          if (groupedData[month]) {
            const amountInBaseCurrency = expense.amount;

            if (expense.type.toLowerCase() === "income") {
              groupedData[month].totalIncome +=
                amountInBaseCurrency / expense.conversionRate;
            } else if (expense.type.toLowerCase() === "expense") {
              groupedData[month].totalExpense +=
                amountInBaseCurrency / expense.conversionRate;
            }
            groupedData[month].profit = Math.max(
              0,
              groupedData[month].totalIncome - groupedData[month].totalExpense
            );
          }
        });
      } else if (selectedDuration === "currentweek") {
        groupedData = currentWeekDays.reduce((acc, day) => {
          acc[day] = { totalIncome: 0, totalExpense: 0, profit: 0 };
          return acc;
        }, {});

        allExpenses.forEach((expense) => {
          const expenseDate = new Date(expense.date);
          if (
            expenseDate.getFullYear() !== currentYear ||
            expenseDate.getMonth() !== currentMonth
          )
            return;

          const day = expenseDate.toLocaleString("default", {
            weekday: "long",
          });

          if (groupedData[day]) {
            const amountInBaseCurrency = expense.amount;
            if (expense.type.toLowerCase() === "income") {
              groupedData[day].totalIncome +=
                amountInBaseCurrency / expense.conversionRate;
            } else if (expense.type.toLowerCase() === "expense") {
              groupedData[day].totalExpense +=
                amountInBaseCurrency / expense.conversionRate;
            }
            groupedData[day].profit = Math.max(
              0,
              groupedData[day].totalIncome - groupedData[day].totalExpense
            );
          }
        });
      } else if (selectedDuration === "last6years") {
        groupedData = getLast6Years().reduce((acc, year) => {
          acc[year] = { totalIncome: 0, totalExpense: 0, profit: 0 };
          return acc;
        }, {});

        allExpenses.forEach((expense) => {
          const expenseYear = new Date(expense.date).getFullYear();
          if (!groupedData[expenseYear]) return;

          const amountInBaseCurrency = expense.amount;

          if (expense.type.toLowerCase() === "income") {
            groupedData[expenseYear].totalIncome +=
              amountInBaseCurrency / expense.conversionRate;
          } else if (expense.type.toLowerCase() === "expense") {
            groupedData[expenseYear].totalExpense +=
              amountInBaseCurrency / expense.conversionRate;
          }
          groupedData[expenseYear].profit = Math.max(
            0,
            groupedData[expenseYear].totalIncome -
              groupedData[expenseYear].totalExpense
          );
        });
      } else if (selectedDuration === "all") {
        // Group data by all available years
        allExpenses.forEach((expense) => {
          const expenseYear = new Date(expense.date).getFullYear().toString();

          if (!groupedData[expenseYear]) {
            groupedData[expenseYear] = {
              totalIncome: 0,
              totalExpense: 0,
              profit: 0,
            };
          }

          const amountInBaseCurrency = expense.amount;

          if (expense.type.toLowerCase() === "income") {
            groupedData[expenseYear].totalIncome +=
              amountInBaseCurrency / expense.conversionRate;
          } else if (expense.type.toLowerCase() === "expense") {
            groupedData[expenseYear].totalExpense +=
              amountInBaseCurrency / expense.conversionRate;
          }

          groupedData[expenseYear].profit = Math.max(
            0,
            groupedData[expenseYear].totalIncome -
              groupedData[expenseYear].totalExpense
          );
        });
      }

      const formattedData = Object.keys(groupedData).map((key) => ({
        day: key,
        totalIncome: groupedData[key].totalIncome,
        totalExpense: groupedData[key].totalExpense,
        profit: groupedData[key].profit,
      }));

      setExpenseData(formattedData);
    } catch (error) {
      console.error("Error fetching or processing data:", error.message);
      setError("Failed to load expense data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (
      selectedProject && selectedMilestone &&
      (selectedDuration === "all" || (startDate && endDate))
    ) {
      fetchFilteredExpenses(selectedProject, selectedMilestone, startDate, endDate);
    }
  }, [selectedProject, selectedMilestone, startDate, endDate]);

  const handleIncomeChange = () => setShowIncome((prev) => !prev);
  const handleExpenseChange = () => setShowExpense((prev) => !prev);
  const handleProfitChange = () => setShowProfit((prev) => !prev);

  if (loading)
    return (
      <div className="flex justify-center items-center h-full">
        <div className="spinner"></div>
      </div>
    );
  if (error) return <p className="text-red-500">Error: {error}</p>;
  const showGraph = showIncome || showExpense || showProfit;
  return (
    <div className="rounded-lg bg-white p-4 lg:h-full font-customFont">
      <div className="flex gap-3">
          <CustomInput
            type="checkbox"
            checked={showIncome}
            onChange={handleIncomeChange}
            className="custom-radioIncome me-1"
            label="Income"
          />
          <CustomInput
            type="checkbox"
            label="Expense"
            checked={showExpense}
            onChange={handleExpenseChange}
            className="custom-radioExpense me-1"
          />
          <CustomInput
            type="checkbox"
            checked={showProfit}
            label="Net Income"
            onChange={handleProfitChange}
            className="custom-radioNetIncome me-1"
          />
      </div>

      <BarChart
        dataset={expenseData.length > 0 ? expenseData : getEmptyDataset()}
        xAxis={[
          {
            scaleType: "band",
            dataKey: "day",
          },
        ]}
        yAxis={[{ scaleType: "linear" }]}
        series={[
          showIncome && {
            dataKey: "totalIncome",
            valueFormatter: (value) => value!=null&&!isNaN(value)?`$${value.toFixed(2)}`:"$0.00",
            color: "#4CAF50",
          },
          showExpense && {
            dataKey: "totalExpense",
            valueFormatter: (value) => value!=null&&!isNaN(value)?`$${value.toFixed(2)}`:"$0.00",
            color: "#FF5733",
          },
          showProfit && {
            dataKey: "profit",
            valueFormatter: (value) => value!=null&&!isNaN(value)?`$${value.toFixed(2)}`:"$0.00",
            color: "#3F51B5",
          },
        ].filter(Boolean)}
        layout="vertical"
        margin={{ right: 30, left: 40 }}
        height={350}
      />
    </div>
  );
};

export default User_ExpenseGraph;
